import { type Field, type FieldsOf, type SchemaObject } from "./types.ts";

/** Quais colunas o primeiro nível justifica mostrar. */
interface Columns {
  required: boolean;
  description: boolean;
}

import { useMemo, useState } from "react";

import { useLocale } from "../lib/i18n.jsx";
import Icon from "../theme/Icon.tsx";

// Tabela de campos de um schema — nome, tipo, obrigatoriedade e descrição.
//
// Complementa o exemplo JSON, que mostra a FORMA mas não o SIGNIFICADO: um
// exemplo não consegue dizer que `searchType` aceita só CREATION ou CLOSING, nem
// que um campo é obrigatório.
//
// ── Degradação graciosa ──
// O spec da Infotravel hoje tem 3.707 propriedades e nenhuma com `description`,
// e só 2 marcadas como `required`. Uma tabela ingênua renderizaria uma coluna
// "Descrição" inteiramente vazia e — pior — marcaria todo campo como "opcional",
// afirmando algo falso sobre `username` e `password`.
//
// Então cada coluna só aparece quando há informação para ela. Conforme os DTOs
// recebem @Schema no backend, a tabela vai se preenchendo sozinha.

const MAX_DEPTH = 6;

interface RowProps {
  field: Field;
  depth: number;
  fieldsOf: FieldsOf;
  columns: Columns;
  path: string;
}

function Row({ field, depth, fieldsOf, columns, path }: RowProps) {
  const [open, setOpen] = useState(false);
  const { t } = useLocale();
  const canExpand = !!field.child && depth < MAX_DEPTH;

  const children = useMemo(
    () => (open && canExpand ? fieldsOf(field.child) : []),
    [open, canExpand, field.child, fieldsOf]
  );

  return (
    <>
      <tr
        className={canExpand ? "st-row st-expandable" : "st-row"}
        onClick={canExpand ? () => setOpen((v) => !v) : undefined}
      >
        <td className="st-name" style={{ paddingLeft: 14 + depth * 16 }}>
          <span className="st-name-inner">
            {canExpand ? (
              <Icon name={open ? "chevron-down" : "chevron-right"} size={11} />
            ) : (
              <span className="st-no-chevron" />
            )}
            <code>{field.name}</code>
          </span>
        </td>
        <td className="st-type mono">{field.type}</td>
        {columns.required && (
          <td className="st-req">{field.required && <span className="st-badge-req">{t("required")}</span>}</td>
        )}
        {columns.description && (
          <td className="st-desc">
            {field.description}
            {field.deprecated && <span className="ref-param-dep">{t("deprecated")}</span>}
            {/* O enum é a informação que o exemplo JSON nunca carrega: ele mostra
                um valor, não o conjunto permitido. */}
            {field.enum && <div className="st-enum mono">{field.enum.map((v) => String(v)).join(" | ")}</div>}
          </td>
        )}
      </tr>

      {open &&
        children.map((child) => (
          <Row
            key={`${path}.${field.name}.${child.name}`}
            field={child}
            depth={depth + 1}
            fieldsOf={fieldsOf}
            columns={columns}
            path={`${path}.${field.name}`}
          />
        ))}
    </>
  );
}

export default function SchemaTable({
  schema,
  fieldsOf,
  title,
}: {
  schema: SchemaObject | null | undefined;
  fieldsOf: FieldsOf;
  title?: string;
}) {
  const { t } = useLocale();
  const fields = useMemo(() => (schema ? fieldsOf(schema) : []), [schema, fieldsOf]);

  // Decide as colunas pelo conteúdo real do primeiro nível. Não desce a árvore
  // para isso: descobrir que existe uma description a 4 níveis não justifica
  // resolver a cadeia inteira só para escolher um cabeçalho.
  const columns = useMemo<Columns>(
    () => ({
      required: fields.some((f) => f.required),
      description: fields.some((f) => f.description || f.enum),
    }),
    [fields]
  );

  if (!fields.length) return null;

  return (
    <div className="st-wrap">
      {title && <div className="ref-subhead">{title}</div>}
      <div className="st-scroll">
        <table className="st-table">
          <thead>
            <tr>
              <th>{t("fieldName")}</th>
              <th>{t("fieldType")}</th>
              {columns.required && <th>{t("fieldRequired")}</th>}
              {columns.description && <th>{t("fieldDescription")}</th>}
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <Row key={f.name} field={f} depth={0} fieldsOf={fieldsOf} columns={columns} path="root" />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
